import React, { useState, useMemo, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Plus, Trash2, Wallet } from "lucide-react";
import { CURRENCIES, getCurrency } from "@/lib/currencies";

const CATS = ["stay", "transit", "dining", "activities", "other"];

export default function BudgetTracker({ trip, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ category: "stay", description: "", amount: 0, date: "" });
  const [editPlan, setEditPlan] = useState(false);
  const [planDraft, setPlanDraft] = useState(trip.budget_planned || {});
  // Default the display currency to whatever the trip was created in (falls back to USD)
  const [viewCur, setViewCur] = useState(trip.currency || "USD");
  const [rates, setRates] = useState({ USD: 1 });

  useEffect(() => {
    api.get("/currency/rates").then(r => setRates(r.data.rates)).catch(() => {});
  }, []);
  const rate = rates[viewCur] || 1;
  const cur = getCurrency(viewCur);
  const sym = cur.symbol.trim() ? cur.symbol : viewCur + " ";
  const fmt = (usd) => `${sym}${(Number(usd || 0) * rate).toLocaleString(undefined, { maximumFractionDigits: rate < 2 ? 2 : 0 })}`;

  const totals = useMemo(() => {
    const t = {}; CATS.forEach(c => t[c] = 0);
    (trip.expenses || []).forEach(e => { t[e.category] = (t[e.category] || 0) + Number(e.amount || 0); });
    return t;
  }, [trip.expenses]);
  const totalSpent = CATS.reduce((s, c) => s + (totals[c] || 0), 0);
  const totalPlanned = CATS.reduce((s, c) => s + Number((trip.budget_planned || {})[c] || 0), 0);

  const addExp = async () => {
    if (!f.description) { toast.error("Description required"); return; }
    try { const { data } = await api.post(`/trips/${trip.trip_id}/expenses`, f); onUpdate(data); setOpen(false); setF({ category: "stay", description: "", amount: 0, date: "" }); toast.success("Expense logged"); }
    catch { toast.error("Failed"); }
  };
  const delExp = async (id) => { try { const { data } = await api.delete(`/trips/${trip.trip_id}/expenses/${id}`); onUpdate(data); } catch { toast.error("Failed"); } };
  const savePlan = async () => {
    try { const { data } = await api.patch(`/trips/${trip.trip_id}`, { budget_planned: planDraft }); onUpdate(data); setEditPlan(false); toast.success("Budget updated"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-serif-display text-2xl font-semibold">Budget tracker</h2>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-stone-200 bg-white">
            <span className="font-mono-code text-[10px] uppercase text-stone-500">Show in</span>
            <select value={viewCur} onChange={(e)=>setViewCur(e.target.value)} data-testid="budget-currency-select" className="text-sm bg-transparent border-0 focus:outline-none font-mono-code max-w-[240px]">
              {CURRENCIES.filter(c => rates[c.code]).map(c => (
                <option key={c.code} value={c.code}>{c.flag} {c.code} — {c.name}</option>
              ))}
            </select>
          </div>
          <Button size="sm" variant="outline" onClick={()=>{ setPlanDraft(trip.budget_planned || {}); setEditPlan(true); }} data-testid="edit-budget-button">Set plan</Button>
          <Button size="sm" onClick={()=>setOpen(true)} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="add-expense-button"><Plus size={14} className="mr-1"/> Log expense</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-stone-200 bg-white p-6">
        <div className="grid md:grid-cols-3 gap-6 items-center">
          <div>
            <div className="font-mono-code text-[10px] uppercase tracking-widest text-stone-500">Planned</div>
            <div className="font-serif-display text-3xl font-bold" data-testid="budget-planned-total">{fmt(totalPlanned)}</div>
          </div>
          <div>
            <div className="font-mono-code text-[10px] uppercase tracking-widest text-stone-500">Spent</div>
            <div className="font-serif-display text-3xl font-bold" data-testid="budget-spent-total">{fmt(totalSpent)}</div>
          </div>
          <div>
            <div className="font-mono-code text-[10px] uppercase tracking-widest text-stone-500">Remaining</div>
            <div className={`font-serif-display text-3xl font-bold ${totalPlanned - totalSpent < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmt(totalPlanned - totalSpent)}</div>
          </div>
        </div>
        {viewCur !== "USD" && (
          <div className="mt-2 font-mono-code text-[10px] text-stone-500">
            Reference rate 1 USD = {rate.toLocaleString(undefined, { maximumFractionDigits: 4 })} {viewCur} · Amounts stored in USD.
          </div>
        )}
        <div className="mt-6 space-y-4">
          {CATS.map(c => {
            const planned = Number((trip.budget_planned || {})[c] || 0);
            const spent = totals[c] || 0;
            const pct = planned > 0 ? Math.min(100, (spent / planned) * 100) : (spent > 0 ? 100 : 0);
            return (
              <div key={c}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="capitalize font-medium">{c}</span>
                  <span className="font-mono-code text-xs text-stone-600">{fmt(spent)} / {fmt(planned)}</span>
                </div>
                <Progress value={pct} className="h-2 bg-stone-100" />
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-lg mb-3">Expense log</h3>
        {(!trip.expenses || trip.expenses.length === 0) ? (
          <div className="text-center py-10 border border-dashed border-stone-300 rounded-2xl bg-white text-stone-500">
            <Wallet size={22} className="mx-auto mb-1 text-stone-400"/>No expenses yet.
          </div>
        ) : (
          <div className="rounded-2xl border border-stone-200 bg-white divide-y divide-stone-100">
            {trip.expenses.map(e => (
              <div key={e.id} className="p-4 flex items-center gap-3 group" data-testid={`expense-${e.id}`}>
                <div className="w-2 h-2 rounded-full bg-[#C85A32]"/>
                <div className="flex-1">
                  <div className="font-semibold">{e.description}</div>
                  <div className="font-mono-code text-xs text-stone-500 capitalize">{e.category} · {e.date || "—"}</div>
                </div>
                <div className="font-mono-code font-semibold">{fmt(e.amount)}</div>
                <button onClick={()=>delExp(e.id)} data-testid={`expense-delete-${e.id}`} className="text-stone-400 hover:text-red-600 opacity-0 group-hover:opacity-100"><Trash2 size={14}/></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-[#FAF7F2]">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">Log expense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Category</Label>
              <select value={f.category} onChange={(e)=>setF({...f, category: e.target.value})} data-testid="expense-category-select" className="mt-1 h-10 w-full rounded-md border border-stone-300 bg-white px-3 text-sm">
                {CATS.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Description</Label><Input value={f.description} onChange={(e)=>setF({...f, description: e.target.value})} className="mt-1" data-testid="expense-description-input"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount (USD)</Label><Input type="number" value={f.amount} onChange={(e)=>setF({...f, amount: Number(e.target.value)})} className="mt-1" data-testid="expense-amount-input"/></div>
              <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e)=>setF({...f, date: e.target.value})} className="mt-1"/></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={addExp} className="bg-[#C85A32] hover:bg-[#B04B26]" data-testid="expense-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editPlan} onOpenChange={setEditPlan}>
        <DialogContent className="max-w-md bg-[#FAF7F2]">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">Planned budget</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {CATS.map(c=>(
              <div key={c} className="grid grid-cols-3 items-center gap-3">
                <Label className="capitalize">{c}</Label>
                <Input type="number" value={planDraft[c] || 0} onChange={(e)=>setPlanDraft({...planDraft, [c]: Number(e.target.value)})} className="col-span-2" data-testid={`plan-${c}-input`}/>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setEditPlan(false)}>Cancel</Button>
            <Button onClick={savePlan} className="bg-[#C85A32] hover:bg-[#B04B26]" data-testid="plan-save-button">Save plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
